/* Copyright (c) 2020-present CloudIO Inc */

import { camelCase } from 'lodash-es';
import {
  controlledBy,
  displayName,
  dsAppUidProp,
  getNextAlias,
  hideDirtyIndicator,
} from '../DataStore/DataStoreManager';
import { Platform } from '../../platform/Platform';
import { getManager } from '../../registry/Manager';
import {
  AliasType,
  ComponentManager,
  ComponentManagerFn,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './ReadOnlyDataStore';
import { emptyFunction } from '@cloudio-saas/statex';

const ReadOnlyDataStoreManager: ComponentManagerFn<'ReadOnlyDataStore'> = (
  platform: Platform,
): ComponentManager<'ReadOnlyDataStore'> => {
  let timer: NodeJS.Timeout;
  let callback = emptyFunction;
  return {
    ...baseComponentManager<'ReadOnlyDataStore'>(platform, 'ReadOnlyDataStore'),
    defaultValue,
    replaceableWith: ['DataStore', 'TransientDataStore', 'LocalDataStore'],
    isChildAllowed: (page, item, childType) =>
      (['TriggerBeforeQuery', 'TriggerAfterQuery'] as ItemType[]).includes(
        childType,
      ),
    onPropertyChange: (property, value, itemData, { appUid, pageId }) => {
      if (property === 'dsAppUid') {
        callback();
        return {
          [property]: value,
          dataSource: '',
          alias: '',
        };
      }
      if (property === 'dataSource' && typeof value === 'string') {
        const map = platform.getDataSources(appUid, pageId);
        const val = getNextAlias(`${camelCase(value)}Alias`, map);
        if (timer) {
          clearTimeout(timer);
        }
        timer = setTimeout(() => {
          platform.resetAliases(appUid, pageId);
        }, 1300);
        return {
          [property]: value,
          alias: val,
        };
      }
      return {
        [property]: value,
      };
    },
    props: {
      dsAppUid: dsAppUidProp(platform),
      dataSource: {
        label: 'Data Source',
        type: 'DataSourceChooser',
        required: true,
        refreshCallback(cb) {
          callback = cb;
        },
      },
      alias: {
        label: 'Data Source Alias',
        type: 'DataSourceAlias',
        required: true,
      },
      displayName,
      autoQuery: {
        label: 'Auto Query',
        type: 'boolean',
      },
      maxRows: {
        label: 'Max Rows',
        type: 'number',
        min: 20,
        max: 2000,
        step: 20,
      },
      filterLocally: {
        label: 'Filter Locally',
        type: 'boolean',
      },
      singleton: {
        label: 'Singleton',
        type: 'boolean',
        helpText:
          'Singleton dataStores defined across different pages, with the same alias, will share the same state.',
      },
      cacheAll: {
        label: 'Cache All',
        type: 'boolean',
        helpText:
          'Check this to cache query result. With cache enabled, duplicate queries are not sent to the server.',
      },
      usePagination: {
        label: 'Use Pagination',
        type: 'boolean',
        helpText: 'Check this to use pagination component on this store.',
      },
      ignorePKDuplicate: {
        label: 'Ignore Primary Keys Duplicates',
        type: 'boolean',
        helpText:
          'Check this to ignore the duplicate primary key values on this store.',
      },
      maskPageOnQuery: {
        label: 'Mask Page On Query',
        type: 'boolean',
        helpText:
          'Check this to mask the page until the query request on store completed.',
      },
      doNotMonitorQueryRequest: {
        label: 'Do not Monitor Query Request',
        type: 'boolean',
        helpText: 'Check to disable monitoring query request.',
      },
      controlledBy,
      hideDirtyIndicator,
    },
    category: 'Data',
    name: 'Readonly Store',
    canHaveChildren: () => true,
    generateShortId: (item) => item.alias,
    isPropertyVisible: (property, item) => {
      switch (property) {
        case 'hideDirtyIndicator':
          return false;
        case 'dsAppUid':
        case 'dataSource':
          return true;
        case 'alias':
          return !!item.dataSource;
        default:
          return !!item.alias;
      }
    },
    getDataSourceAliasProperty: (type: AliasType) =>
      type === 'STORE' || type === 'QUERY_FILTER'
        ? { aliasProp: 'alias' }
        : undefined,
    getDisplayText: (item, expanded, { appUid, pageId }) => {
      if (!item.props.alias) return '';
      const page = platform.getPage(appUid, pageId);
      const itemIds = Object.keys(page);
      const usage = itemIds.filter((id) => {
        const itm = page[id];
        const mgr = getManager(itm.type);
        return mgr.isDataSourceAliasUsed(itm, item.props.alias);
      });
      return usage.length
        ? `${item.props.alias} (${usage.length} usage${
            usage.length > 1 ? 's' : ''
          })`
        : item.props.alias;
    },
    onCopy: (props, samePage) => ({
      ...props,
      alias: samePage ? '' : props.alias,
    }),
    icon: { iconName: 'database' },
    description: `Read only store is similar to data store component but without insert/update/delete operations. All the insert/update/delete allowed flags will be set to false on ReadOnly Store.

\`\`\`alert
Leave the auto query off, if you assign this store to a select component. Otherwise, the store will be queried twice.
\`\`\``,
  };
};

export default ReadOnlyDataStoreManager;
